import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Plane, ArrowRight, Phone, Mail, MapPin } from "lucide-react";

const PHONE_DISPLAY = "+91 90911 00200";
const PHONE_TEL = "+919091100200";

export default function FinalCTA() {
  const sectionRef = useRef();
  const inView = useInView(sectionRef, { margin: "-100px" });
  const [launched, setLaunched] = useState(false);

  useEffect(() => {
    if (inView) setLaunched(true);
  }, [inView]);

  return (
    <section
      ref={sectionRef}
      id="contact"
      data-testid="final-cta-section"
      className="relative py-28 md:py-36 overflow-hidden bg-offwhite"
    >
      {/* soft gradient backdrop */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          background:
            "radial-gradient(ellipse at 20% 0%, rgba(0,153,204,0.12), transparent 50%), radial-gradient(ellipse at 80% 100%, rgba(212,175,55,0.10), transparent 50%)",
        }} />
      </div>

      {/* Animated airplane trail */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {launched && (
          <div className="absolute bottom-24 left-10 animate-takeoff">
            <Plane className="w-12 h-12 text-sky-brand -rotate-45" strokeWidth={1.5} style={{ filter: "drop-shadow(0 0 12px rgba(0,153,204,0.6))" }} />
          </div>
        )}
        <div className="absolute bottom-28 left-0 w-full h-px bg-gradient-to-r from-transparent via-sky-brand/40 to-transparent" />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-navy-deep/10 mb-7 soft-shadow">
            <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.28em] text-navy-deep font-semibold">Free Consultation</span>
          </div>

          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] max-w-4xl mx-auto text-navy-deep">
            Start your <span className="text-gradient-navy">journey</span><br />
            in the next 60 seconds.
          </h2>
          <p className="mt-7 max-w-2xl mx-auto text-lg text-slate-600 leading-relaxed">
            Tell us where you want to go. Our visa & travel specialists will get back within 2 hours with a custom plan.
          </p>
        </motion.div>

        <ContactForm />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          <ContactCard icon={Phone} label="Call us anytime" value={PHONE_DISPLAY} href={`tel:${PHONE_TEL}`} testid="contact-phone" />
          <ContactCard icon={Mail} label="Drop a mail" value="travelfator01@gmail.com" href="mailto:travelfator01@gmail.com" testid="contact-email" />
          <ContactCard icon={MapPin} label="Visit office" value={<>Raghuleela Mall, Kandivali West,<br/>Mumbai 400067</>} testid="contact-address" />
        </div>
      </div>
    </section>
  );
}

function ContactCard({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div data-testid={testid} className="bg-white rounded-2xl p-5 text-left border border-navy-deep/8 soft-shadow hover:premium-shadow transition-all duration-300 hover:-translate-y-0.5 h-full">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-soft flex items-center justify-center shrink-0">
          <Icon className="w-4.5 h-4.5 text-sky-brand" strokeWidth={2.2} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] mb-1 font-bold text-gold-dark">{label}</div>
          <div className="font-heading text-base font-semibold text-navy-deep">{value}</div>
        </div>
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}

function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "US Tourist Visa" });

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <motion.form
      onSubmit={onSubmit}
      data-testid="contact-form"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, delay: 0.2 }}
      className="mt-12 max-w-3xl mx-auto bg-white rounded-3xl p-6 md:p-8 text-left border border-navy-deep/8 premium-shadow"
    >
      {submitted ? (
        <div data-testid="form-success" className="py-10 text-center">
          <div className="w-14 h-14 rounded-full gradient-navy mx-auto mb-5 flex items-center justify-center shadow-lg">
            <Plane className="w-6 h-6 text-white -rotate-45" strokeWidth={2.5} />
          </div>
          <h3 className="font-heading text-2xl font-bold mb-2 text-navy-deep">Request received.</h3>
          <p className="text-slate-600">A specialist will reach out within 2 hours.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full Name" id="name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Email" id="email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Phone" id="phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2 font-semibold">Service</label>
              <select
                data-testid="form-service"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="w-full bg-offwhite border border-navy-deep/10 rounded-xl px-4 py-3.5 text-navy-deep focus:outline-none focus:border-sky-brand focus:bg-white transition-colors"
              >
                <option>US Tourist Visa</option>
                <option>Student Visa</option>
                <option>Business Visa</option>
                <option>Tour Package</option>
                <option>Documentation</option>
              </select>
            </div>
          </div>
          <button
            type="submit"
            data-testid="form-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-full gradient-navy text-white font-semibold tracking-tight shadow-[0_12px_32px_-8px_rgba(0,51,102,0.45)] hover:shadow-[0_20px_44px_-10px_rgba(0,51,102,0.6)] hover:scale-[1.01] transition-all"
          >
            Request Free Consultation
            <ArrowRight className="w-4 h-4" />
          </button>
        </>
      )}
    </motion.form>
  );
}

function Field({ label, id, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2 font-semibold">{label}</label>
      <input
        data-testid={`form-${id}`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={label}
        required
        className="w-full bg-offwhite border border-navy-deep/10 rounded-xl px-4 py-3.5 text-navy-deep placeholder:text-slate-400 focus:outline-none focus:border-sky-brand focus:bg-white transition-colors"
      />
    </div>
  );
}
